import { call, put, takeEvery, select } from 'redux-saga/effects';
import { LOCATION_CHANGE, createMatchSelector, push } from 'connected-react-router';
import { actions, constants } from './redux';
import selector from './selector';
import { didDocumentLocationChange } from '../../utils/router';
import Api from '../../utils/Api';
import { addSuccess, addError } from '../notifications/actions';

const pathConfig = { path: '/users/:id/:tab', exact: true };


export function* fetchItem({ payload }) {
  try {
    const data = yield call(Api.users.show, payload);

    yield put(actions.addLoadedItem(data));
  } catch (e) {
    yield put(actions.loadItemError(e));
    console.error(e);
  }
}

export function* updateItem({ payload }) {
  try {
    const match = yield select(createMatchSelector(pathConfig));

    const data = yield call(Api.users.update, match.params.id, payload);

    yield put(actions.addLoadedItem(data));
    yield put(addSuccess('The user updated successfully'));
    yield put({ type: '@USER_DETAILS_PAGE/LOAD_ITEM_REQUEST', payload: match.params.id });
  } catch (e) {
    yield put(addError('Update user error'));

    console.error(e);
  }
}

export function* fetchItemPageInit(action) {
  const { didLoad } = yield select(selector);
  // Not `exact`: every overlay this page owns lives one or two
  // segments deeper (`…/new`, `…/:itemId/edit`, `…/bulk`), and an
  // exact match fetched nothing for them. Clicking a dialog open hid
  // it — the card was already in the store — but opening one by URL
  // left it on "Loading, please wait…" for good (CREW-1277).
  const match = didDocumentLocationChange(action, { path: '/users/:id/:tab' });

  if (match && !didLoad) {
    yield put(actions.loadItemRequest(match.params.id));
  }
}

export function* fetchOwnerPoolHistory({ payload: { userId } }) {
  try {
    const response = yield call(Api.users.getOwnerPoolHistory, userId);
    yield put(actions.setOwnerPoolHistory(response.history || []));
  } catch (e) {
    console.error(e);
    yield put(actions.setOwnerPoolError(e.message || 'Failed to load pool history'));
  }
}

export function* assignToOwnerPool({ payload: { userId, ownerId, onSuccess } }) {
  try {
    yield put(actions.setOwnerPoolLoading(true));
    yield call(Api.users.assignToOwnerPool, userId, ownerId);
    yield put(actions.setOwnerPoolLoading(false));
    yield put(actions.toggleOwnerPoolDialog({ dialog: 'showAssignDialog', show: false }));
    
    yield put(addSuccess('Successfully assigned to owner pool'));

    // Reload user data
    yield put(actions.loadItemRequest(userId));
    
    if (onSuccess) onSuccess();
  } catch (e) {
    console.error(e);
    const errorMsg = e?.response?.data?.errors?.error || e.message || 'Failed to assign to owner pool';
    yield put(actions.setOwnerPoolError(errorMsg));
    yield put(actions.setOwnerPoolLoading(false));
    yield put(addError(errorMsg));
  }
}

export function* removeFromOwnerPool({ payload: { userId, removalReasonId, additionalReasonIds, onSuccess } }) {
  try {
    yield put(actions.setOwnerPoolLoading(true));
    yield call(Api.users.removeFromOwnerPool, userId, removalReasonId, additionalReasonIds || []);
    yield put(actions.setOwnerPoolLoading(false));
    yield put(actions.toggleOwnerPoolDialog({ dialog: 'showAssignDialog', show: false }));
    
    yield put(addSuccess('Successfully removed from owner pool'));

    // Reload user data
    yield put(actions.loadItemRequest(userId));
        
    if (onSuccess) onSuccess();
  } catch (e) {
    console.error(e);
    const errorMsg = e?.response?.data?.errors?.error || e.message || 'Failed to remove from owner pool';
    yield put(actions.setOwnerPoolError(errorMsg));
    yield put(actions.setOwnerPoolLoading(false));
    yield put(addError(errorMsg));
  }
}

/**
 * getFeatureSagas (rename)
 * each saga file should return all feature sagas
 * @returns {ForkEffect[]}
 */
export default function getFeatureSagas() {
  return [
    takeEvery(constants.LOAD_ITEM_REQUEST, fetchItem),
    takeEvery(LOCATION_CHANGE, fetchItemPageInit),
    takeEvery(constants.UPDATE_ITEM_REQUEST, updateItem),
    takeEvery([
      constants.FETCH_OWNER_POOL_HISTORY_REQUEST,
    ], fetchOwnerPoolHistory),
    takeEvery([
      constants.ASSIGN_TO_OWNER_POOL_REQUEST,
    ], assignToOwnerPool),
    takeEvery([
      constants.REMOVE_FROM_OWNER_POOL_REQUEST,
    ], removeFromOwnerPool),
  ];
}
